import React, {useEffect, useRef, useState} from 'react';
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  TouchableOpacity,
  ScrollView
} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {getStatisticsItemsPerMinuteDetails} from '../../../actions/statisticsAction';
import {
  ChartLoading,
  NoDataDisplay,
  GridLines,
  YAxisLabels
} from './ChartComponents';
import {
  processItemsPerMinuteStatistics,
  generateYAxisValues,
  calculateDailyChartDimensions,
  calculateOptimalDisplayTime
} from './chartUtils';
import AutoScrollWrapper from './AutoScrollWrapper';
import usePolling from './usePolling';
import useChartAnimations from './useChartAnimations';
import {ChartContent} from './svgComponents';

const ItemsPerMinuteChart = ({updateAutoTime, autoScrollEnabled = true, metricType = 'items'}) => {
  const dispatch = useDispatch();
  const response = useSelector(state => state.statistics.itemsPerMinuteList);

  const [containerDimensions, setContainerDimensions] = useState({
    width: Dimensions.get('window').width * 0.9,
    height: 450
  });

  const [seriesByPacker, setSeriesByPacker] = useState({all: []});
  const [displayData, setDisplayData] = useState([]);
  const [packerOptions, setPackerOptions] = useState([]);
  const [selectedPacker, setSelectedPacker] = useState('all');
  const [maxValue, setMaxValue] = useState(0);
  const [loading, setLoading] = useState(!response?.data?.statistics);
  const [barWidth, setBarWidth] = useState(80);
  const [spacing, setSpacing] = useState(20);
  const [chartWidth, setChartWidth] = useState(0);
  const [shouldScroll, setShouldScroll] = useState(false);

  const animations = useChartAnimations(displayData, 800, 300, 100);
  const updateAutoTimeRef = useRef(updateAutoTime);
  const lastAutoTimeRef = useRef(null);

  useEffect(() => {
    updateAutoTimeRef.current = updateAutoTime;
  }, [updateAutoTime]);

  const maxBarsToShow = 6;
  const barGapRatio = 0.2;
  const yAxisWidth = 50;
  const segmentsCount = 6;

  const onContainerLayout = event => {
    const {width, height} = event.nativeEvent.layout;
    setContainerDimensions({width, height});
  };

  usePolling(() => {
    dispatch(getStatisticsItemsPerMinuteDetails());
  }, 5000);

  useEffect(() => {
    if (!response?.data) {
      setLoading(false);
      return;
    }

    const result = processItemsPerMinuteStatistics(response, metricType);
    if (!result.success) {
      setSeriesByPacker({all: []});
      setDisplayData([]);
      setPackerOptions([]);
      setMaxValue(0);
      setLoading(false);
      return;
    }

    setSeriesByPacker(result.seriesByPacker || {all: []});
    setPackerOptions(result.packers || []);
    setLoading(false);
  }, [response?.data?.statistics, metricType]);

  useEffect(() => {
    if (selectedPacker === 'all') {
      return;
    }

    if (!seriesByPacker[selectedPacker]) {
      setSelectedPacker('all');
    }
  }, [seriesByPacker, selectedPacker]);

  useEffect(() => {
    const selectedSeries = seriesByPacker[selectedPacker] || [];
    setDisplayData(selectedSeries);

    if (selectedSeries.length) {
      const maxVal = Math.max(...selectedSeries.map(item => item.value || 0));
      setMaxValue(Math.ceil(maxVal * 1.1));
    } else {
      setMaxValue(0);
    }
  }, [seriesByPacker, selectedPacker]);

  useEffect(() => {
    if (!displayData.length) {
      setShouldScroll(false);
      return;
    }

    const dimensions = calculateDailyChartDimensions(
      displayData,
      containerDimensions,
      maxBarsToShow,
      barGapRatio,
      yAxisWidth
    );

    setBarWidth(dimensions.barWidth);
    setSpacing(dimensions.spacing);
    setChartWidth(dimensions.chartWidth);
    setShouldScroll(dimensions.shouldScroll);

    const optimalTime = calculateOptimalDisplayTime(
      displayData,
      'itemsPerMinute',
      maxBarsToShow,
      dimensions.barWidth + dimensions.spacing
    );

    if (
      updateAutoTimeRef.current &&
      typeof updateAutoTimeRef.current === 'function' &&
      optimalTime !== lastAutoTimeRef.current
    ) {
      lastAutoTimeRef.current = optimalTime;
      updateAutoTimeRef.current(optimalTime);
    }
  }, [displayData, containerDimensions]);

  const handleSelectPacker = id => {
    setSelectedPacker(id);
  };

  const chartContainerHeight = containerDimensions.height * 0.8;
  const adjustedChartHeight = chartContainerHeight - 10;
  const labelSpaceHeight = 60;

  const renderFilters = () => {
    if (!packerOptions.length) {
      return null;
    }

    const allOption = {id: 'all', label: 'All Packers'};

    return (
      <View style={styles.filtersContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filtersScroll}>
          {[allOption, ...packerOptions].map(option => {
            const isActive = selectedPacker === option.id;
            return (
              <TouchableOpacity
                key={option.id}
                onPress={() => handleSelectPacker(option.id)}
                style={[
                  styles.filterChip,
                  isActive && styles.filterChipActive
                ]}>
                <Text
                  style={[
                    styles.filterChipText,
                    isActive && styles.filterChipTextActive
                  ]}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <ChartLoading />;
    }

    if (!displayData.length) {
      return (
        <View style={styles.mainContent}>
          {renderFilters()}
          <NoDataDisplay />
        </View>
      );
    }

    return (
      <View style={styles.mainContent}>
        {renderFilters()}
        <View style={styles.chartArea}>
          <View style={styles.chartRow}>
            <YAxisLabels
              values={generateYAxisValues(maxValue || 1, segmentsCount)}
              chartHeight={adjustedChartHeight}
              segmentsCount={segmentsCount}
              containerStyle={{height: adjustedChartHeight + labelSpaceHeight}}
            />
            <AutoScrollWrapper
              enabled={shouldScroll && autoScrollEnabled}
              speed={35}
              pauseAtEnds={2500}
              showIndicators={shouldScroll}
              style={[
                styles.chartBackground,
                {height: adjustedChartHeight + labelSpaceHeight}
              ]}>
              <View
                style={[
                  styles.svgContainer,
                  {
                    height: adjustedChartHeight + labelSpaceHeight,
                    width: chartWidth
                  }
                ]}>
                <ChartContent
                  chartHeight={adjustedChartHeight}
                  chartWidth={chartWidth}
                  labelSpaceHeight={labelSpaceHeight}
                  segmentsCount={segmentsCount}
                  data={displayData}
                  barWidth={barWidth}
                  spacing={spacing}
                  maxValue={maxValue || 1}
                  animations={animations}
                  GridLines={GridLines}
                  adaptiveValueFont
                />
              </View>
            </AutoScrollWrapper>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container} onLayout={onContainerLayout}>
      {renderContent()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#191919'
  },
  mainContent: {
    flex: 1,
    width: '100%',
    height: '100%',
    paddingVertical: 10,
    paddingBottom: 40,
    paddingHorizontal: 5
  },
  chartArea: {
    flex: 1,
    height: '100%'
  },
  chartRow: {
    flexDirection: 'row',
    height: '100%'
  },
  chartBackground: {
    flex: 1,
    backgroundColor: '#191919'
  },
  svgContainer: {
    borderRadius: 3,
    overflow: 'visible'
  },
  filtersContainer: {
    width: '100%',
    marginBottom: 15
  },
  filtersScroll: {
    paddingHorizontal: 10
  },
  filterChip: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#3A3F47',
    marginRight: 10
  },
  filterChipActive: {
    backgroundColor: '#FFFFFF'
  },
  filterChipText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontFamily: 'Poppins_600SemiBold'
  },
  filterChipTextActive: {
    color: '#000000'
  }
});

export default ItemsPerMinuteChart;
